import axios, { AxiosResponse, AxiosInstance } from 'axios';

class ApiService {
  getPublicTrends() {
    return this.http.get(`${this.apiRoot()}/public/stats/trends`);
  }

  getCongresses(params?: { limit?: number; offset?: number }) {
    const p = params || {};
    const url = `${this.apiV1Root()}/congress`;
    return this.http.get(url, { params: p }).then((r: AxiosResponse) => r.data);
  }

  getCongressDetail(congress: number, params?: { top_n?: number; members_limit?: number; members_offset?: number }) {
    const p = params || {};
    const url = `${this.apiV1Root()}/congress/${congress}`;
    return this.http.get(url, { params: p }).then((r: AxiosResponse) => r.data);
  }

  getChatConfig() {
    const url = `${this.apiV1Root()}/chat/config`;
    return this.http.get(url).then((r: AxiosResponse) => r.data);
  }
  private baseURL: string;
  private token: string | null = null;
  private http: AxiosInstance;

  constructor() {
    // Base URL for API v1; prefer env, but force same-origin on production web host to avoid TLS/CORS on api.askthebill.ai
    const envBase = process.env.REACT_APP_API_BASE_URL;
    this.baseURL = envBase || '/api/v1';
    try {
      const host = window.location && window.location.hostname;
      if (host && (host === 'askthebill.ai' || host === 'www.askthebill.ai')) {
        // Force same-origin paths so frontend Nginx proxies /api and /api/v1
        this.baseURL = '/api/v1';
      }
    } catch {}

    // Create a dedicated Axios instance with auto-fallback on network/TLS errors to same-origin /api or /api/v1
    this.http = axios.create();
    this.http.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        try {
          const cfg: any = error?.config || {};
          if (!error?.response && cfg && !cfg.__retried) {
            let absUrl = '';
            if (typeof cfg.url === 'string' && /^https?:\/\//i.test(cfg.url)) {
              absUrl = cfg.url;
            } else if (typeof cfg.baseURL === 'string' && /^https?:\/\//i.test(cfg.baseURL)) {
              const base = new URL(cfg.baseURL);
              const path = (cfg.url || '').startsWith('/')
                ? cfg.url
                : (base.pathname.replace(/\/$/, '') + '/' + (cfg.url || ''));
              absUrl = base.origin + path;
            }
            if (absUrl) {
              const u = new URL(absUrl);
              const fallbackPath = u.pathname + (u.search || '');
              cfg.__retried = true;
              cfg.baseURL = undefined;
              cfg.url = fallbackPath;
              return await this.http.request(cfg);
            }
          }
        } catch (_) {}
        return Promise.reject(error);
      }
    );

    // Also apply the same fallback to the global axios instance, so any direct axios calls benefit
    axios.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        try {
          const cfg: any = error?.config || {};
          if (!error?.response && cfg && !cfg.__retried) {
            let absUrl = '';
            if (typeof cfg.url === 'string' && /^https?:\/\//i.test(cfg.url)) {
              absUrl = cfg.url;
            } else if (typeof cfg.baseURL === 'string' && /^https?:\/\//i.test(cfg.baseURL)) {
              const base = new URL(cfg.baseURL);
              const path = (cfg.url || '').startsWith('/') ? cfg.url : (base.pathname.replace(/\/$/, '') + '/' + (cfg.url || ''));
              absUrl = base.origin + path;
            }
            if (absUrl) {
              const u = new URL(absUrl);
              const fallbackPath = u.pathname + (u.search || '');
              cfg.__retried = true;
              cfg.baseURL = undefined;
              cfg.url = fallbackPath;
              return await axios.request(cfg);
            }
          }
        } catch (_) {}
        return Promise.reject(error);
      }
    );
  }

  // Public endpoints (no auth)
  async postSupportMessage(payload: { name?: string | null; email?: string | null; subject?: string | null; message: string }) {
    const url = `${this.apiV1Root()}/public/support`;
    return this.http.post(url, payload);
  }

  // Civic Response Studio
  async postCivicResponseCommentReply(payload: { originalComment: string; perspective?: string | null; platform?: string | null; topicHint?: string | null }) {
    const url = `${this.apiV1Root()}/civic-response-studio/comment-reply`;
    return this.http.post(url, payload).then((r: AxiosResponse) => r.data);
  }

  // Admin: Civic Response Studio analytics
  async getCivicResponseMetrics(days: number = 14): Promise<AxiosResponse> {
    const url = `${this.apiV1Root()}/admin/civic-response/metrics?days=${days}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  // Admin: Refresh member profiles missing name or photo
  async refreshMissingMemberProfiles(): Promise<AxiosResponse> {
    const url = `${this.apiV1Root()}/admin/members/refresh-missing-profiles`;
    return this.http.post(url, {}, { headers: this.getAuthHeaders() }).then((r: AxiosResponse) => r);
  }
  async listCivicResponseQueries(params: { q?: string; perspective?: string; platform?: string; start?: string; end?: string; limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.q) qp.append('q', params.q);
    if (params.perspective) qp.append('perspective', params.perspective);
    if (params.platform) qp.append('platform', params.platform);
    if (params.start) qp.append('start', params.start);
    if (params.end) qp.append('end', params.end);
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const url = `${this.apiV1Root()}/admin/civic-response/queries${qp.toString() ? `?${qp.toString()}` : ''}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }
  async updateCivicResponseModeration(queryId: number, payload: { moderation_flag?: string; moderation_reason?: string }): Promise<AxiosResponse> {
    const url = `${this.apiV1Root()}/admin/civic-response/queries/${queryId}`;
    return this.http.patch(url, payload, { headers: this.getAuthHeaders() });
  }

  // Admin: Support messages
  async listSupportMessages(params: { status?: string; limit?: number; offset?: number } = {}) {
    const qp = new URLSearchParams();
    if (params.status) qp.append('status', params.status);
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/admin/support/messages${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async updateSupportMessage(id: number, payload: { status?: string }) {
    return axios.patch(`${this.baseURL}/admin/support/messages/${id}`, payload, { headers: this.getAuthHeaders() });
  }

  // Derive the non-versioned API root (/api) from baseURL for health/info/public endpoints
  private apiRoot(): string {
    if (this.baseURL.endsWith('/api/v1')) {
      return this.baseURL.replace(/\/api\/v1$/, '/api');
    }
    try {
      const u = new URL(this.baseURL);
      u.pathname = '/api';
      return u.toString().replace(/\/$/, '');
    } catch {
      return '/api';
    }
  }

  // Derive a versioned API root (/api/v1) suitable for same-origin fallback
  private apiV1Root(): string {
    try {
      // If baseURL is already a relative path, prefer it
      if (!/^https?:\/\//i.test(this.baseURL)) {
        return this.baseURL;
      }
      // Otherwise construct a relative same-origin path
      return '/api/v1';
    } catch {
      return '/api/v1';
    }
  }

  private isNetworkOnlyError(e: any): boolean {
    return !e || !e.response; // no response means likely TLS/CORS/network
  }

  private async getWithFallback(primaryUrl: string, fallbackUrl: string, options: any = {}) {
    try {
      return await this.http.get(primaryUrl, options);
    } catch (e: any) {
      if (this.isNetworkOnlyError(e)) {
        try {
          return await this.http.get(fallbackUrl, options);
        } catch (e2) {
          throw e2;
        }
      }
      throw e;
    }
  }

  private async postWithFallback(primaryUrl: string, fallbackUrl: string, data?: any, options: any = {}) {
    try {
      return await this.http.post(primaryUrl, data, options);
    } catch (e: any) {
      if (this.isNetworkOnlyError(e)) {
        try {
          return await this.http.post(fallbackUrl, data, options);
        } catch (e2) {
          throw e2;
        }
      }
      throw e;
    }
  }

  // PUBLIC endpoints (proxy via /api to work with Nginx config)
  async getPublicStatsSummary() {
    const base = this.apiRoot();
    return axios.get(`${base}/public/stats/summary`);
  }

  async getTrendingIssues(limit: number = 8) {
    const base = this.apiRoot();
    return axios.get(`${base}/public/issues/trending?limit=${limit}`);
  }

  async getIssueArticle(slug: string) {
    const base = this.apiRoot();
    return axios.get(`${base}/public/issues/article/${encodeURIComponent(slug)}`);
  }

  async getPublicBills(params: { filter?: 'latest'|'passed'|'failed'|'hot'; cursor?: string; limit?: number } = {}) {
    const base = this.apiRoot();
    const qp = new URLSearchParams();
    if (params.filter) qp.append('filter', params.filter);
    if (params.cursor) qp.append('cursor', params.cursor);
    if (params.limit) qp.append('limit', String(params.limit));
    const qs = qp.toString();
    return axios.get(`${base}/public/bills${qs ? `?${qs}` : ''}`);
  }

  async getPublicLawmakers(params: { state?: string; chamber?: string; party?: string; limit?: number } = {}) {
    const base = this.apiRoot();
    const qp = new URLSearchParams();
    if (params.state) qp.append('state', params.state);
    if (params.chamber) qp.append('chamber', params.chamber);
    if (params.party) qp.append('party', params.party);
    if (params.limit) qp.append('limit', String(params.limit));
    const qs = qp.toString();
    return axios.get(`${base}/public/lawmakers${qs ? `?${qs}` : ''}`);
  }

  async getSearchSuggest(q: string) {
    const base = this.apiRoot();
    return axios.get(`${base}/public/search/suggest?q=${encodeURIComponent(q)}`);
  }

  async getLatestVotes(limit: number = 10) {
    const base = this.apiRoot();
    return axios.get(`${base}/public/votes/latest?limit=${limit}`);
  }

  async getTrendingFollowedBills(limit: number = 20) {
    const base = this.apiRoot();
    return axios.get(`${base}/public/trending/followed-bills?limit=${limit}`);
  }

  setToken(token: string | null) {
    this.token = token;
  }

  private getAuthHeaders() {
    const headers: any = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    try {
      const v = window.localStorage.getItem('abt_variant');
      if (v === 'A' || v === 'B') headers['X-Experiment-Variant'] = v;
    } catch {}
    return headers;
  }

  // App endpoints
  async getAppInfo(): Promise<AxiosResponse> {
    // Prefer configured API host; on network/TLS failure, fallback to same-origin /api
    const primary = `${this.apiRoot()}/info`;
    const fallback = `/api/info`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders() });
  }

  async getHealthStatus(): Promise<AxiosResponse> {
    // Prefer configured API host; on network/TLS failure, fallback to same-origin /api
    const primary = `${this.apiRoot()}/health`;
    const fallback = `/api/health`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders(), timeout: 5000 });
  }

  // Bills endpoints
  async getBills(params: {
    limit?: number;
    offset?: number;
    congress?: number;
    billType?: string;
    q?: string;
    view?: string;
  }): Promise<AxiosResponse> {
    // Build primary URL against configured baseURL (may be cross-origin)
    let primary = `${this.baseURL}/bill`;
    if (params.congress && params.billType) {
      primary += `/${params.congress}/${params.billType}`;
    } else if (params.congress) {
      primary += `/${params.congress}`;
    }
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', params.limit.toString());
    if (params.offset !== undefined) qp.append('offset', params.offset.toString());
    if (params.q) qp.append('q', params.q);
    if (params.view) qp.append('view', params.view);
    const qs = qp.toString();
    if (qs) primary += `?${qs}`;

    // Same-origin fallback path for TLS/CORS/network failures
    let fallback = `${this.apiV1Root()}/bill`;
    if (params.congress && params.billType) {
      fallback += `/${params.congress}/${params.billType}`;
    } else if (params.congress) {
      fallback += `/${params.congress}`;
    }
    if (qs) fallback += `?${qs}`;

    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders() });
  }

  async getBillDetail(congress: number, billType: string, billNumber: string, opts: { forceRefresh?: boolean } = {}): Promise<AxiosResponse> {
    const url = `${this.baseURL}/bill/${congress}/${billType}/${billNumber}`;
    const finalUrl = opts.forceRefresh ? `${url}?force_refresh=true` : url;
    return axios.get(finalUrl, {
      headers: this.getAuthHeaders(),
    });
  }

  async postBillAISummary(congress: number, billType: string, billNumber: string, opts: { mode?: 'eli5'|'expert'|'pros_cons' } = {}): Promise<AxiosResponse> {
    const qs = opts.mode ? `?mode=${encodeURIComponent(opts.mode)}` : '';
    const url = `${this.baseURL}/bill/${congress}/${billType}/${billNumber}/ai-summary${qs}`;
    return axios.post(url, null, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillActions(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/actions`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillText(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/text`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillAmendments(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/amendments`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillSummaries(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/summaries`, {
      headers: this.getAuthHeaders(),
    });
  }

  // --- User Engagement APIs ---
  async follow(type: 'bill'|'member', id: number, tags?: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/follow`, { type, id, tags }, { headers: this.getAuthHeaders() });
    }

  // Convenience: follow/unfollow by natural keys
  async followBillByKey(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/follow/bill/${congress}/${billType}/${billNumber}`, null, { headers: this.getAuthHeaders() });
  }
  async unfollowBillByKey(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/follow/bill/${congress}/${billType}/${billNumber}`, { headers: this.getAuthHeaders() });
  }
  async followMemberByBioguide(bioguideId: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/follow/member/${bioguideId}`, null, { headers: this.getAuthHeaders() });
  }
  async unfollowMemberByBioguide(bioguideId: string): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/follow/member/${bioguideId}`, { headers: this.getAuthHeaders() });
  }

  // Convenience: bookmark bill by natural keys
  async addBookmarkBillByKey(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/bookmark/bill/${congress}/${billType}/${billNumber}`, null, { headers: this.getAuthHeaders() });
  }
  async deleteBookmarkBillByKey(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/bookmark/bill/${congress}/${billType}/${billNumber}`, { headers: this.getAuthHeaders() });
  }

  // --- User Analytics ---
  async getMyInsights(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/insights/me/insights`, { headers: this.getAuthHeaders() });
  }

  // Comments (feature-gated on backend)
  async createComment(billId: number, body: string, parentId?: number): Promise<AxiosResponse> {
    const payload: any = { bill_id: billId, body };
    if (parentId !== undefined) payload.parent_id = parentId;
    return axios.post(`${this.baseURL}/comments`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteComment(commentId: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/comments/${commentId}`, { headers: this.getAuthHeaders() });
  }
  async listComments(billId: number, limit: number = 50, offset: number = 0): Promise<AxiosResponse> {
    const params = new URLSearchParams({ bill_id: String(billId), limit: String(limit), offset: String(offset) });
    return axios.get(`${this.baseURL}/comments?${params.toString()}`);
  }

  // Reactions (feature-gated on backend)
  async addReaction(targetType: 'bill'|'comment', targetId: number, reactionType: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/reactions`, { target_type: targetType, target_id: targetId, reaction_type: reactionType }, { headers: this.getAuthHeaders() });
  }
  async removeReaction(targetType: 'bill'|'comment', targetId: number, reactionType: string): Promise<AxiosResponse> {
    const params = new URLSearchParams({ target_type: targetType, target_id: String(targetId), reaction_type: reactionType });
    return axios.delete(`${this.baseURL}/reactions?${params.toString()}`, { headers: this.getAuthHeaders() });
  }
  async getReactionSummary(targetType: 'bill'|'comment', targetId: number): Promise<AxiosResponse> {
    const params = new URLSearchParams({ target_type: targetType, target_id: String(targetId) });
    return axios.get(`${this.baseURL}/reactions/summary?${params.toString()}`);
  }

  async unfollow(type: 'bill'|'member', id: number): Promise<AxiosResponse> {
    const params = new URLSearchParams({ type, id: String(id) });
    return axios.delete(`${this.baseURL}/follow?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  async listMyFollows(type?: 'bill'|'member', limit: number = 50, offset: number = 0): Promise<AxiosResponse> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return axios.get(`${this.baseURL}/me/follows?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  async getMyFeed(limit: number = 50, offset: number = 0, since?: string): Promise<AxiosResponse> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (since) params.append('since', since);
    return axios.get(`${this.baseURL}/me/feed?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  async getMyNotifications(limit: number = 50, offset: number = 0, since?: string): Promise<AxiosResponse> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (since) params.append('since', since);
    return axios.get(`${this.baseURL}/me/notifications?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  async markMyNotificationsRead(payload: { since?: string; ids?: string[] } = {}): Promise<AxiosResponse> {
    return axios.patch(`${this.baseURL}/me/notifications/read`, payload, { headers: this.getAuthHeaders() });
  }

  async addBookmark(type: 'bill'|'member'|'summary', id: number): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/bookmark`, { type, id }, { headers: this.getAuthHeaders() });
  }

  async deleteBookmark(type: 'bill'|'member'|'summary', id: number): Promise<AxiosResponse> {
    const params = new URLSearchParams({ type, id: String(id) });
    return axios.delete(`${this.baseURL}/bookmark?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  async listMyBookmarks(type?: 'bill'|'member'|'summary', limit: number = 50, offset: number = 0): Promise<AxiosResponse> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return axios.get(`${this.baseURL}/me/bookmarks?${params.toString()}`, { headers: this.getAuthHeaders() });
  }

  // Public Profiles
  async getPublicProfile(handle: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/profile/${encodeURIComponent(handle)}`);
  }
  async getMyProfile(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/profile`, { headers: this.getAuthHeaders() });
  }
  async upsertMyProfile(payload: { handle: string; visibility: 'public'|'private'; bio?: string | null }): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/me/profile`, payload, { headers: this.getAuthHeaders() });
  }

  async getBillCommittees(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/committees`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillCosponsors(congress: number, billType: string, billNumber: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/cosponsors${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillRelatedBills(congress: number, billType: string, billNumber: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/relatedbills${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillSubjects(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/subjects`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getBillTitles(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/titles`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Laws
  async getLawsByCongress(congress: number, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/law/${congress}${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getLawsByCongressAndType(congress: number, lawType: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/law/${congress}/${lawType}${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getLawDetail(congress: number, lawType: string, lawNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/law/${congress}/${lawType}/${lawNumber}`, { headers: this.getAuthHeaders() });
  }

  // Committees endpoints
  async getCommittees(params: { limit?: number; offset?: number }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/committee${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getCommitteesByChamber(chamber: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/committee/${chamber}${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getCommitteeDetail(chamber: string, committeeCode: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/committee/${chamber}/${committeeCode}`, { headers: this.getAuthHeaders() });
  }

  async getCommitteeBills(chamber: string, committeeCode: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/committee/${chamber}/${committeeCode}/bills${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getCommitteeMeetings(chamber: string, committeeCode: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/committee/${chamber}/${committeeCode}/meetings${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getCommitteeVotes(chamber: string, committeeCode: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/committee/${chamber}/${committeeCode}/votes${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  // Members endpoints
  async getMembers(params: { limit?: number; offset?: number; q?: string; state?: string }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    if (params.q && params.q.trim()) queryParams.append('q', params.q.trim());
    if (params.state && params.state.trim()) queryParams.append('state', params.state.trim());
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/member${qs ? `?${qs}` : ''}` , {
      headers: this.getAuthHeaders(),
    });
  }

  async getMemberDetail(bioguideId: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/member/${bioguideId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getMemberSponsoredLegislation(bioguideId: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/member/${bioguideId}/sponsored-legislation${qs ? `?${qs}` : ''}` , {
      headers: this.getAuthHeaders(),
    });
  }

  async getMemberCosponsoredLegislation(bioguideId: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/member/${bioguideId}/cosponsored-legislation${qs ? `?${qs}` : ''}` , {
      headers: this.getAuthHeaders(),
    });
  }

  async getMemberVotes(bioguideId: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.offset !== undefined) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/member/${bioguideId}/votes${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getMemberAISummary(bioguideId: string, opts: { forceRefresh?: boolean } = {}): Promise<AxiosResponse> {
    const url = `${this.baseURL}/member/${bioguideId}/ai-summary`;
    const finalUrl = opts.forceRefresh ? `${url}?force_refresh=true` : url;
    return axios.get(finalUrl, { headers: this.getAuthHeaders() });
  }

  async getMemberSocialPosts(bioguideId: string, params: { limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
    const qs = queryParams.toString();
    return axios.get(`${this.baseURL}/member/${bioguideId}/social-posts${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Admin-only: start interrogation job for a member (sync sponsored and cosponsored bills)
  async startMemberInterrogation(bioguideId: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/member/${bioguideId}/interrogate`, {}, { headers: this.getAuthHeaders() });
  }

  // Admin-only: poll interrogation status
  async getMemberInterrogationStatus(bioguideId: string, jobId: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/member/${bioguideId}/interrogate/${jobId}/status`, { headers: this.getAuthHeaders() });
  }

  // Chat endpoints
  async sendChatMessage(params: {
    message: string;
    sessionId?: string;
    billContext?: string[];
    memberContext?: string[];
  }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/chat/message`, params, {
      headers: this.getAuthHeaders(),
    });
  }

  async createChatSession(billContext: string[] = [], memberContext: string[] = []): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/chat/sessions`, { billContext, memberContext }, {
      headers: this.getAuthHeaders(),
    });
  }

  async getChatSessions(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/chat/sessions`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getChatHistory(sessionId: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/chat/sessions/${sessionId}/messages`, {
      headers: this.getAuthHeaders(),
    });
  }

  async deleteChatSession(sessionId: string): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/chat/sessions/${sessionId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async updateChatSessionContext(sessionId: string, billContext: string[] = [], memberContext: string[] = []): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/chat/sessions/${sessionId}/context`, { billContext, memberContext }, {
      headers: this.getAuthHeaders(),
    });
  }

  async listMemberSocialProfiles(params: { query?: string; limit?: number; offset?: number } = {}): Promise<AxiosResponse> {
    const usp = new URLSearchParams();
    if (params.query) usp.append('query', params.query);
    if (params.limit !== undefined) usp.append('limit', String(params.limit));
    if (params.offset !== undefined) usp.append('offset', String(params.offset));
    const qs = usp.toString();
    return axios.get(`${this.baseURL}/member/social-profiles${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  // Admin-only: upsert social handles for a member
  async upsertMemberSocialHandles(
    bioguideId: string,
    payload: { x_handle?: string | null; facebook_handle?: string | null; instagram_handle?: string | null; tiktok_handle?: string | null; truth_social_handle?: string | null }
  ): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/member/${bioguideId}/social-handles`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  // Admin endpoints
  async getAdminMetrics(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/metrics`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getAdminEngagementInsights(days: number = 7): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (days) qp.append('days', String(days));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/admin/insights${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Admin: AdSense config
  async getAdminAdsenseConfig(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/adsense/config`, { headers: this.getAuthHeaders() });
  }
  async updateAdminAdsenseConfig(payload: { enabled: boolean; publisher_id?: string | null; auto_ads?: boolean; slot_map?: Record<string,string> }): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/admin/adsense/config`, payload, { headers: this.getAuthHeaders() });
  }
  async getAdminAdsenseMetrics(window_hours: number = 24): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (window_hours) qp.append('window_hours', String(window_hours));
    return axios.get(`${this.baseURL}/admin/adsense/metrics?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }

  async triggerRefreshBillSummaries(): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/ai/bills/refresh-all`, null, {
      headers: this.getAuthHeaders(),
    });
  }

  async getRefreshBillSummariesStatus(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/ai/bills/refresh-all/status`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getAdminSyncStatus(): Promise<AxiosResponse> {
    const primary = `${this.baseURL}/admin/sync/status`;
    const fallback = `${this.apiV1Root()}/admin/sync/status`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders(), timeout: 8000 });
  }

  // Admin long-running actions
  async triggerRefreshMemberSummaries(): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/actions/refresh-member-summaries`, {}, { headers: this.getAuthHeaders() });
  }

  async getRefreshMemberSummariesStatus(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/actions/refresh-member-summaries/status`, { headers: this.getAuthHeaders() });
  }

  async triggerSyncBills(): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/actions/sync-bills`, {}, { headers: this.getAuthHeaders() });
  }

  async getSyncBillsStatus(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/actions/sync-bills/status`, { headers: this.getAuthHeaders() });
  }

  async postAdminDedupe(): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/maintenance/dedupe`, {}, { headers: this.getAuthHeaders() });
  }

  async getAdminDedupeStatus(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/maintenance/dedupe/status`, { headers: this.getAuthHeaders() });
  }

  // Admin: Sync shards and ingest controls
  async getSyncShards(): Promise<AxiosResponse> {
    const primary = `${this.baseURL}/admin/sync/shards`;
    const fallback = `${this.apiV1Root()}/admin/sync/shards`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders(), timeout: 8000 });
  }
  async postIngestShard(kind: 'bill'|'house-vote'|'committee-meeting', shard: string): Promise<AxiosResponse> {
    const primary = `${this.baseURL}/admin/sync/ingest-shard`;
    const fallback = `${this.apiV1Root()}/admin/sync/ingest-shard`;
    return this.postWithFallback(primary, fallback, { kind, shard }, { headers: this.getAuthHeaders() });
  }
  async getIngestShardStatus(jobId?: string): Promise<AxiosResponse> {
    const qs = jobId ? `?job_id=${encodeURIComponent(jobId)}` : '';
    const primary = `${this.baseURL}/admin/sync/ingest-shard/status${qs}`;
    const fallback = `${this.apiV1Root()}/admin/sync/ingest-shard/status${qs}`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders() });
  }

  // Admin: Queue inspection
  async getSyncQueue(kind: 'bill'|'member'|'maintenance', params: { limit?: number; include_dlq?: boolean } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    if (params.include_dlq) qp.append('include_dlq', 'true');
    const qs = qp.toString();
    const primary = `${this.baseURL}/admin/sync/queue/${encodeURIComponent(kind)}${qs ? `?${qs}` : ''}`;
    const fallback = `${this.apiV1Root()}/admin/sync/queue/${encodeURIComponent(kind)}${qs ? `?${qs}` : ''}`;
    return this.getWithFallback(primary, fallback, { headers: this.getAuthHeaders() });
  }

  async getAdminSummarySchedule(params: { limit?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.limit !== undefined) qp.append('limit', String(params.limit));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/admin/summaries/schedule${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Admin: Celery queue maintenance
  async purgeCeleryQueues(payload: { queues: string[]; confirm: boolean }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/celery/queues/purge`, payload, { headers: this.getAuthHeaders() });
    }
  async moveCeleryMessages(payload: { source: string; target: string; max?: number }): Promise<AxiosResponse> {
    const body = { ...payload, max: payload.max ?? 10000 };
    return axios.post(`${this.baseURL}/admin/celery/queues/move`, body, { headers: this.getAuthHeaders() });
  }
  async getCeleryWorkers(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/celery/workers`, { headers: this.getAuthHeaders() });
  }
  async assignCeleryMessages(payload: { source: string; worker: string; max?: number; queue_name?: string; ensure_consumer?: boolean }): Promise<AxiosResponse> {
    const body = { ensure_consumer: true, max: 1000, ...payload };
    return axios.post(`${this.baseURL}/admin/celery/queues/assign`, body, { headers: this.getAuthHeaders() });
  }

  // Admin: AI Summaries queue status
  async getSummaryQueueStatus(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/admin/ai/summaries/status`, { headers: this.getAuthHeaders() });
  }

  // Ollama endpoints (admin)
  async getOllamaEndpoints(params: { refresh?: boolean } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.refresh) qp.append('refresh', 'true');
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/admin/ollama/endpoints${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async addOllamaEndpoint(payload: { base_url: string; label?: string; enabled?: boolean }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/admin/ollama/endpoints`, payload, { headers: this.getAuthHeaders() });
  }
  async updateOllamaEndpoint(id: number, payload: { base_url?: string; label?: string; enabled?: boolean }): Promise<AxiosResponse> {
    return axios.patch(`${this.baseURL}/admin/ollama/endpoints/${id}`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteOllamaEndpoint(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/admin/ollama/endpoints/${id}`, { headers: this.getAuthHeaders() });
  }

  // Nominations endpoints
  async getNominations(params: { limit?: number; offset?: number; congress?: number } = {}): Promise<AxiosResponse> {
    const { congress, limit, offset } = params;
    let url = `${this.baseURL}/nomination`;
    if (congress) {
      url += `/${congress}`;
    }
    const qp = new URLSearchParams();
    if (limit !== undefined) qp.append('limit', String(limit));
    if (offset !== undefined) qp.append('offset', String(offset));
    const qs = qp.toString();
    return axios.get(`${url}${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }

  async getNominationDetail(congress: number, nominationNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/nomination/${congress}/${nominationNumber}`, { headers: this.getAuthHeaders() });
  }

  async getNominationActions(congress: number, nominationNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/nomination/${congress}/${nominationNumber}/actions`, { headers: this.getAuthHeaders() });
  }

  async getNominationCommittees(congress: number, nominationNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/nomination/${congress}/${nominationNumber}/committees`, { headers: this.getAuthHeaders() });
  }

  async getNominationHearings(congress: number, nominationNumber: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/nomination/${congress}/${nominationNumber}/hearings`, { headers: this.getAuthHeaders() });
  }

  async getNominationOrdinalNominees(congress: number, nominationNumber: string, ordinal: number | string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/nomination/${congress}/${nominationNumber}/${ordinal}`, { headers: this.getAuthHeaders() });
  }

  // --- Personalization: Saved Searches ---
  async listSavedSearches(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/saved-searches`, { headers: this.getAuthHeaders() });
  }
  async createSavedSearch(payload: { name: string; query: any; is_public?: 'public'|'private' }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/saved-searches`, payload, { headers: this.getAuthHeaders() });
  }
  async updateSavedSearch(id: number, payload: { name?: string; query?: any; is_public?: 'public'|'private' }): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/me/saved-searches/${id}`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteSavedSearch(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/saved-searches/${id}`, { headers: this.getAuthHeaders() });
  }
  async getFeedFromSavedSearch(savedSearchId: number, limit: number = 50, offset: number = 0): Promise<AxiosResponse> {
    const qp = new URLSearchParams({ saved_search_id: String(savedSearchId), limit: String(limit), offset: String(offset) });
    return axios.get(`${this.baseURL}/me/saved-search-feed?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }

  // --- Personalization: Collections ---
  async listCollections(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/collections`, { headers: this.getAuthHeaders() });
  }
  async createCollection(payload: { slug: string; name: string; description?: string | null; visibility?: 'public'|'private' }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/collections`, payload, { headers: this.getAuthHeaders() });
  }
  async updateCollection(id: number, payload: { name?: string; description?: string | null; visibility?: 'public'|'private' }): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/me/collections/${id}`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteCollection(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/collections/${id}`, { headers: this.getAuthHeaders() });
  }
  async listCollectionItems(collectionId: number): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/collections/${collectionId}/items`, { headers: this.getAuthHeaders() });
  }
  async addCollectionItem(collectionId: number, payload: { target_type: 'bill'|'member'; target_id: number; sort_order?: number | null }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/collections/${collectionId}/items`, payload, { headers: this.getAuthHeaders() });
  }
  async removeCollectionItem(collectionId: number, itemId: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/collections/${collectionId}/items/${itemId}`, { headers: this.getAuthHeaders() });
  }
  async getPublicCollection(handle: string, slug: string): Promise<AxiosResponse> {
    // Public endpoint is exposed at /c/:handle/:slug under both root and /api
    // Use apiRoot to allow cross-origin configured host with fallback
    const primary = `${this.apiRoot()}/c/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`;
    const fallback = `/api/c/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`;
    return this.getWithFallback(primary, fallback, {});
  }

  // --- Personalization: Alerts ---
  async listAlerts(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/alerts`, { headers: this.getAuthHeaders() });
  }
  async createAlert(payload: { topic: 'bill'|'member'|'tag'|'committee'; target?: string | null; event: 'status'|'hearing'|'vote'; channel?: 'email'|'webpush' | null; params?: any }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/alerts`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteAlert(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/alerts/${id}`, { headers: this.getAuthHeaders() });
  }

  // --- Personalization: Notes ---
  async listNotes(params: { bill_id?: number } = {}): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.bill_id !== undefined) qp.append('bill_id', String(params.bill_id));
    const qs = qp.toString();
    return axios.get(`${this.baseURL}/me/notes${qs ? `?${qs}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async createNote(payload: { bill_id: number; text: string; version?: string | null; start_offset?: number | null; end_offset?: number | null }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/notes`, payload, { headers: this.getAuthHeaders() });
  }
  async updateNote(id: number, payload: { text?: string; version?: string | null; start_offset?: number | null; end_offset?: number | null }): Promise<AxiosResponse> {
    return axios.put(`${this.baseURL}/me/notes/${id}`, payload, { headers: this.getAuthHeaders() });
  }
  async deleteNote(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/notes/${id}`, { headers: this.getAuthHeaders() });
  }
  // Generic notes by target (new)
  async listNotesByTarget(target_type: 'bill'|'member'|'committee'|'nomination'|'amendment', target_ref: string): Promise<AxiosResponse> {
    const qp = new URLSearchParams({ target_type, target_ref });
    return axios.get(`${this.baseURL}/me/notes/target?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }
  async createNoteForTarget(payload: { target_type: 'bill'|'member'|'committee'|'nomination'|'amendment'; target_ref: string; text: string; version?: string | null; start_offset?: number | null; end_offset?: number | null; bill_id?: number | null }): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/notes/target`, payload, { headers: this.getAuthHeaders() });
  }

  // --- New: Bill Diff ---
  async getBillDiff(
    congress: number,
    billType: string,
    billNumber: string,
    params: { versionA: string; versionB: string; mode?: 'lines'|'words' }
  ): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    qp.append('versionA', params.versionA);
    qp.append('versionB', params.versionB);
    if (params.mode) qp.append('mode', params.mode);
    return axios.get(`${this.baseURL}/bill/${congress}/${billType}/${billNumber}/diff?${qp.toString()}`, {
      headers: this.getAuthHeaders(),
    });
  }

  // --- New: Compare endpoints ---
  async compareBillsByIds(ids: string[]): Promise<AxiosResponse> {
    const usp = new URLSearchParams();
    usp.append('ids', ids.join(','));
    return axios.get(`${this.baseURL}/compare/bills?${usp.toString()}`, { headers: this.getAuthHeaders() });
  }
  async compareMembers(ids: string[]): Promise<AxiosResponse> {
    const usp = new URLSearchParams();
    usp.append('ids', ids.join(','));
    return axios.get(`${this.baseURL}/compare/members?${usp.toString()}`, { headers: this.getAuthHeaders() });
  }

  // --- New: Tags taxonomy ---
  async listTags(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/tags`, { headers: this.getAuthHeaders() });
  }
  async getTag(slug: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/tags/${encodeURIComponent(slug)}`, { headers: this.getAuthHeaders() });
  }

  // --- New: Calendar ICS (download as blob) ---
  async getCalendarIcs(): Promise<AxiosResponse<Blob>> {
    return axios.get(`${this.baseURL}/me/calendar.ics`, { headers: this.getAuthHeaders(), responseType: 'blob' });
  }

  // --- Power Users: Webhooks ---
  async listMyWebhooks(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/webhooks`, { headers: this.getAuthHeaders() });
  }
  async createMyWebhook(event: 'bill.updated'|'vote.recorded', target_url: string, secret?: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/me/webhooks`, { event, target_url, secret }, { headers: this.getAuthHeaders() });
  }
  async deleteMyWebhook(id: number): Promise<AxiosResponse> {
    return axios.delete(`${this.baseURL}/me/webhooks/${id}`, { headers: this.getAuthHeaders() });
  }

  // --- Graph ---
  async getSponsorGraph(bioguide: string): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/graph/sponsors?bioguide=${encodeURIComponent(bioguide)}`);
  }
  async getSimilarMembers(bioguide: string, top: number = 10): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/graph/similar?bioguide=${encodeURIComponent(bioguide)}&top=${top}`);
  }

  // --- Exports & RSS helpers ---
  getBillExportJsonUrl(congress: number, billType: string, billNumber: string): string {
    return `${this.baseURL}/bill/${congress}/${billType}/${billNumber}/export.json`;
  }
  getBillExportCsvUrl(congress: number, billType: string, billNumber: string): string {
    return `${this.baseURL}/bill/${congress}/${billType}/${billNumber}/export.csv`;
  }
  getBillRssUrl(congress: number, billType: string, billNumber: string): string {
    // RSS is served off the root /rss, not versioned
    const base = this.apiRoot().replace(/\/api$/, '');
    return `${base}/rss/bill/${congress}/${billType}/${billNumber}`;
  }

  // --- Personalization: Location ---
  async getMyLocation(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/me/location`, { headers: this.getAuthHeaders() });
  }
  async setMyLocation(zip: string, state?: string | null, district?: string | null): Promise<AxiosResponse> {
    const payload: any = { zip };
    if (state) payload.state = state;
    if (district) payload.district = district;
    return axios.post(`${this.baseURL}/me/location`, payload, { headers: this.getAuthHeaders() });
  }

  // --- Quiz: Vote Alignment ---
  async getVoteAlignmentQuiz(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/quiz/vote-alignment`, { headers: this.getAuthHeaders() });
  }
  async submitVoteAlignmentQuiz(statementIds: number[], answers: number[]): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/quiz/vote-alignment/submit`, { statement_ids: statementIds, answers }, { headers: this.getAuthHeaders() });
  }
  async getVoteAlignmentResult(): Promise<AxiosResponse> {
    return axios.get(`${this.baseURL}/quiz/vote-alignment/result`, { headers: this.getAuthHeaders() });
  }

  // --- Community: Polls on Bills ---
  async getBillPollSummary(congress: number, billType: string, billNumber: string): Promise<AxiosResponse> {
    // Public endpoint; include auth if present to return userChoice
    return axios.get(`${this.apiV1Root()}/polls/bill/${congress}/${billType}/${billNumber}`, { headers: this.getAuthHeaders() });
  }
  async voteBillPoll(congress: number, billType: string, billNumber: string, choice: 'support'|'oppose'|'unsure'): Promise<AxiosResponse> {
    return axios.post(`${this.apiV1Root()}/polls/bill/${congress}/${billType}/${billNumber}/vote`, { choice }, { headers: this.getAuthHeaders() });
  }

  // --- Public: ZIP lookup (no auth) ---
  async getPublicLocationByZip(zip: string): Promise<AxiosResponse> {
    const base = this.apiRoot();
    return axios.get(`${base}/public/location/zip/${encodeURIComponent(zip)}`);
  }

  // --- FEC Finance ---
  async getMemberFEC(bioguideId: string, cycle?: number, candidateId?: string): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (cycle) qp.append('cycle', String(cycle));
    if (candidateId) qp.append('candidate_id', candidateId);
    return axios.get(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec${qp.toString() ? `?${qp.toString()}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async getMemberFECSummary(bioguideId: string, cycle?: number, candidateId?: string): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (cycle) qp.append('cycle', String(cycle));
    if (candidateId) qp.append('candidate_id', candidateId);
    return axios.get(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/summary${qp.toString() ? `?${qp.toString()}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async getMemberFECBenchmarks(bioguideId: string, cycle?: number, candidateId?: string): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (cycle) qp.append('cycle', String(cycle));
    if (candidateId) qp.append('candidate_id', candidateId);
    return axios.get(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/benchmarks${qp.toString() ? `?${qp.toString()}` : ''}`, { headers: this.getAuthHeaders() });
  }
  async getMemberFECTrends(bioguideId: string, maxCycles: number = 6, candidateId?: string): Promise<AxiosResponse> {
    const qp = new URLSearchParams({ max_cycles: String(maxCycles) });
    if (candidateId) qp.append('candidate_id', candidateId);
    return axios.get(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/trends?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }
  getMemberFECFilingsCsvUrl(bioguideId: string, cycle?: number): string {
    const qp = new URLSearchParams();
    if (cycle) qp.append('cycle', String(cycle));
    return `${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/filings.csv${qp.toString() ? `?${qp.toString()}` : ''}`;
  }
  getMemberFECCommitteesCsvUrl(bioguideId: string, cycle?: number): string {
    const qp = new URLSearchParams();
    if (cycle) qp.append('cycle', String(cycle));
    return `${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/committees.csv${qp.toString() ? `?${qp.toString()}` : ''}`;
  }
  async refreshMemberFEC(bioguideId: string): Promise<AxiosResponse> {
    return axios.post(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/refresh`, {}, { headers: this.getAuthHeaders() });
  }
  async fecSearchCandidates(params: { q?: string; state?: string; office?: string; district?: string; cycle?: number }): Promise<AxiosResponse> {
    const qp = new URLSearchParams();
    if (params.q) qp.append('q', params.q);
    if (params.state) qp.append('state', params.state);
    if (params.office) qp.append('office', params.office);
    if (params.district) qp.append('district', params.district);
    if (params.cycle) qp.append('cycle', String(params.cycle));
    return axios.get(`${this.baseURL}/member/fec/candidates/search?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }
  async fecMapMember(bioguideId: string, fec_candidate_id: string, reason?: string): Promise<AxiosResponse> {
    const payload: any = { fec_candidate_id };
    if (reason) payload.reason = reason;
    return axios.post(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/map`, payload, { headers: this.getAuthHeaders() });
  }

  async fecGetMappingAudit(bioguideId: string, limit: number = 20): Promise<AxiosResponse> {
    const qp = new URLSearchParams({ limit: String(limit) });
    return axios.get(`${this.baseURL}/member/${encodeURIComponent(bioguideId)}/fec/mapping/audit?${qp.toString()}`, { headers: this.getAuthHeaders() });
  }
}
 
export const apiService = new ApiService();
